import React, { useEffect, useMemo, useRef, useState } from 'react';
import AvatarImg from './AvatarImg';
import { type Mood } from '../Assets/Avatars';

export type CoachState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'all-pass'; passCount: number }
  | { kind: 'partial-fail'; passCount: number; totalCount: number; consecutiveFails: number }
  | { kind: 'error'; message?: string };

interface Props {
  state: CoachState;
  /** Optional hint pool — picked from when partial-fail / repeated-fail. */
  hints?: string[];
}

interface CoachLine { mood: Mood; msg: string; sub?: string; }

function lineFor(state: CoachState, hints: string[]): CoachLine {
  switch (state.kind) {
    case 'idle':
      return {
        mood: 'idle',
        msg: 'hey! poke me if you get stuck.',
        sub: 'I cycle through hints when tests start failing.',
      };

    case 'running':
      return { mood: 'nervous', msg: 'running your code…' };

    case 'all-pass':
      return {
        mood: 'happy',
        msg: state.passCount > 0
          ? `nice! ${state.passCount} / ${state.passCount} passing. submit for the bonus 🎉`
          : 'nice! all tests pass. submit for the bonus 🎉',
      };

    case 'partial-fail': {
      const { passCount, totalCount, consecutiveFails } = state;
      if (consecutiveFails >= 5) {
        return {
          mood: 'stressed',
          msg: `${passCount} / ${totalCount} passing — let's slow down.`,
          sub: hints[(consecutiveFails - 1) % Math.max(1, hints.length)] ?? 'try walking through one failing test on paper.',
        };
      }
      if (consecutiveFails >= 3) {
        return {
          mood: 'nervous',
          msg: `${passCount} / ${totalCount} passing.`,
          sub: hints[(consecutiveFails - 1) % Math.max(1, hints.length)] ?? 'check your edge cases.',
        };
      }
      return {
        mood: 'nervous',
        msg: `close! ${passCount} / ${totalCount} passing.`,
        sub: 'expand a failing case to see what you got vs expected.',
      };
    }

    case 'error':
      return {
        mood: 'sad',
        msg: 'yikes — code blew up before tests ran.',
        sub: state.message ? state.message.slice(0, 120) : 'open the console tab for the full traceback.',
      };
  }
}

const DEFAULT_HINTS = [
  'try a smaller input by hand and watch what your code does.',
  'is it an off-by-one? indices vs lengths?',
  'edge cases: empty input, single element, duplicates, negatives.',
  'are you mutating something you should be copying?',
  'what does your code return for the smallest failing example?',
  'sketch the algorithm in plain english first, then translate.',
];

const Coach: React.FC<Props> = ({ state, hints }) => {
  const pool = hints && hints.length > 0 ? hints : DEFAULT_HINTS;
  const line = useMemo(() => lineFor(state, pool), [state, pool]);

  const [minimized, setMinimized] = useState(false);
  const [pulse, setPulse] = useState(0);
  const lastKindRef = useRef<string>(state.kind);

  // Pop back open + briefly pulse the bubble whenever state.kind transitions.
  useEffect(() => {
    if (state.kind !== lastKindRef.current) {
      lastKindRef.current = state.kind;
      setMinimized(false);
      setPulse(p => p + 1);
    }
  }, [state.kind]);

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        aria-label="show coach"
        style={{
          position: 'absolute',
          right: 14,
          bottom: 172,
          width: 48,
          height: 48,
          padding: 0,
          background: 'var(--paper)',
          border: '1.4px solid var(--ink)',
          borderRadius: '50%',
          boxShadow: '2px 3px 0 var(--ink)',
          cursor: 'pointer',
          zIndex: 10,
          overflow: 'hidden',
        }}
      >
        <AvatarImg size={44} mood={line.mood} />
      </button>
    );
  }

  return (
    <div
      className="ide-coach"
      key={pulse}
      style={{
        position: 'absolute',
        right: 14,
        bottom: 172,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 6,
        zIndex: 10,
        animation: 'coach-pop 0.32s ease-out',
      }}
    >
      <div
        className="ide-coach-bubble"
        style={{ position: 'relative', maxWidth: 220, pointerEvents: 'auto' }}
      >
        <button
          onClick={() => setMinimized(true)}
          aria-label="hide coach"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 16,
            height: 16,
            padding: 0,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink-faint)',
            fontSize: 12,
            lineHeight: '14px',
          }}
        >
          ×
        </button>
        <div style={{ paddingRight: 12 }}>{line.msg}</div>
        {line.sub && (
          <div style={{ fontSize: 10, color: 'var(--ink-soft)', marginTop: 4, fontStyle: 'italic' }}>
            {line.sub}
          </div>
        )}
      </div>
      <AvatarImg size={56} mood={line.mood} style={{ pointerEvents: 'none' }} />
      <style>{`
        @keyframes coach-pop {
          0%   { transform: translateY(8px); opacity: 0; }
          60%  { transform: translateY(-2px); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Coach;
